import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Moment } from 'moment';
import moment from 'moment-timezone';
import { DatePicker } from 'antd';

import { LabeledField } from '~components/form-fields/labeled-field';
import { Section } from '~components/form-fields/section';
import { useFeaturedStore } from '~contexts/mobx';
import { FeaturedForm } from '~stores/featured';
import style from './style.module.scss';

interface ScheduleSectionProps {
    values: FeaturedForm;
    setFieldValue: (field: string, value: unknown) => void;
    checkIfHasError: (path: string) => boolean;
}

export const ScheduleSection = observer(
    ({ values, setFieldValue, checkIfHasError }: ScheduleSectionProps) => {
        const featuredStore = useFeaturedStore();

        useEffect(() => {
            featuredStore.fetchActiveDatesMap();
        }, [featuredStore]);

        const dateFullCellRender = (date: Moment) => {
            const dateKey = date.format('YYYY-MM-DD');
            const activeBannersCount =
                featuredStore.activeDatesMap.get(dateKey) || 0;
            const isToday = date.isSame(moment(), 'day');

            return (
                <div className={style.calendar_cell}>
                    <div
                        className={`${style.date_number} ${
                            isToday ? style.today : ''
                        }`}
                    >
                        {date.date()}
                    </div>
                    {activeBannersCount > 0 && (
                        <div className={style.active_indicator}>
                            <span className={style.dot}></span>
                            {activeBannersCount > 1 && (
                                <span className={style.count}>
                                    {activeBannersCount}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            );
        };

        return (
            <Section title="Schedule">
                <div className={style.schedule_grid}>
                    <LabeledField
                        label="Start date"
                        helperText="The date when the featured item becomes visible in the app."
                        hasError={checkIfHasError('startDate')}
                    >
                        <DatePicker
                            allowClear={false}
                            format="YYYY-MM-DD hh:mm A"
                            showTime={{ format: 'HH:mm', minuteStep: 15 }}
                            value={values.startDate}
                            onChange={(date: Moment | null) =>
                                setFieldValue('startDate', date)
                            }
                            disabledDate={() => false}
                            dateRender={dateFullCellRender}
                        />
                    </LabeledField>
                    <LabeledField
                        label="End date"
                        helperText="The date when the featured item will be automatically removed from the app."
                        hasError={checkIfHasError('endDate')}
                    >
                        <DatePicker
                            allowClear={false}
                            format="YYYY-MM-DD hh:mm A"
                            showTime={{ format: 'HH:mm', minuteStep: 15 }}
                            value={values.endDate}
                            onChange={(date: Moment | null) =>
                                setFieldValue('endDate', date)
                            }
                            disabledDate={(date: Moment | null) => {
                                if (!date) return false;
                                if (
                                    values.startDate &&
                                    date.isBefore(values.startDate, 'minute')
                                )
                                    return true;
                                return false;
                            }}
                            dateRender={dateFullCellRender}
                        />
                    </LabeledField>
                </div>
            </Section>
        );
    }
);
